import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import FileUploadCard from "../FileUploadCard";
import FileSelectionCard from "../FileUploadCard/FileSelectionCard";
import AppendInfoSection from "./AppendInfoSection";

import { DATA_APPEND_CONSTANTS } from "../../constants/global.constants";

import { uploadAppendFile } from "../../network/cipAPI";
const FileHandler = ({
  uploadedFile,
  setUploadedFile,
  onFileValidation,
  handleAppendExistingFile,
  handleAppendRemove,
  lastUploadedDataset,
  setLastUploadedDataset,
  selectedTrain = 1,
}) => {
  const { t } = useTranslation();
  const [isProceed, setIsProceed] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setIsUploadComplete] = useState(false);

  // Regex to match expected columns: [col1, col2], Received columns from the resposne
  const EXPECTED_COLUMNS_REGEX =
    /Expected columns:\s*\[(.*?)\],?\s*Received columns/;
  // Regex to match received columns: [col1, col2], Missing columns  from the resposne
  const RECEIVED_COLUMNS_REGEX =
    /Received columns:\s*\[(.*?)\],?\s*Missing columns/;
  // Regex to match missing columns: [col1, col2]  from the resposne
  const MISSING_COLUMNS_REGEX =
    /Missing columns:\s*\[(.*?)\](?:\s*$|[^\[\]]*$)/;

  // Helper to parse columns from a matched group
  const parseColumns = (match) =>
    match?.[1]
      ? match[1].split(",").map((col) => col.trim().replace(/^['"]|['"]$/g, ""))
      : [];

  /**
   * Parses error messages for column mismatch details.
   * Returns structured error info if possible, otherwise returns the original message.
   */
  const parseErrorMessage = (errorMsg) => {
    try {
      const expectedMatch = errorMsg.match(EXPECTED_COLUMNS_REGEX);
      const foundMatch = errorMsg.match(RECEIVED_COLUMNS_REGEX);
      const missingMatch = errorMsg.match(MISSING_COLUMNS_REGEX);

      if (!(expectedMatch && foundMatch)) {
        return { originalMessage: errorMsg, hasStructuredError: false };
      }

      return {
        expected: parseColumns(expectedMatch),
        received: parseColumns(foundMatch),
        missing: parseColumns(missingMatch),
        hasStructuredError: true,
      };
    } catch {
      return { originalMessage: errorMsg, hasStructuredError: false };
    }
  };

  const renderFormattedError = (errorInfo) => {
    if (!errorInfo?.hasStructuredError) {
      return (
        <div>
          {errorInfo?.originalMessage || t("ValueStrings.somethingWentWrong")}
        </div>
      );
    }

    const expected = errorInfo.expected || [];
    const received = errorInfo.received || [];
    const missing = errorInfo.missing || [];

    return (
      <div>
        <div>{t("dataAppend.columnMismatch")}</div>
        <div>
          {t("dataAppend.expected")} [
          {expected.map((col, index) => (
            <span
              key={col}
              className={missing.includes(col) ? "missing-column" : undefined}
            >
              {col}
              {index < expected.length - 1 && ", "}
            </span>
          ))}
          ]
        </div>
        <div>
          {t("dataAppend.found")}[{received.join(", ")}]
        </div>
      </div>
    );
  };

  const handleFileUpload = (file) => {
    if (file) {
      setUploadedFile(file);
      setIsUploadComplete(false); // Reset upload complete status when new file is selected
    }
  };

  const uploadFile = async (file) => {
    if (!file) return { error: t("dataAppend.noFileError") };

    setIsUploading(true);
    try {
      const chunkSize = DATA_APPEND_CONSTANTS.CHUNK_SIZE; // 3MB per chunk
      const totalChunks = Math.ceil(file.size / chunkSize);
      const fileName = file.name;

      // Upload all chunks except the last one with commit_block_list=false
      const promiseList = [];
      for (let chunkIndex = 0; chunkIndex < totalChunks - 1; chunkIndex++) {
        const chunk = file.slice(
          chunkIndex * chunkSize,
          (chunkIndex + 1) * chunkSize,
        );
        const formData = new FormData();
        formData.append("File", chunk, fileName);

        promiseList.push(
          uploadAppendFile(
            formData,
            chunkIndex + 1,
            totalChunks,
            selectedTrain,
            false,
            lastUploadedDataset?.appendId,
          ),
        );
      }

      // Wait for all chunks except last one
      await Promise.all(promiseList);

      // Upload the last chunk with commit_block_list=false first
      const lastChunk = file.slice(
        (totalChunks - 1) * chunkSize,
        totalChunks * chunkSize,
      );
      const formData = new FormData();
      formData.append("File", lastChunk, fileName);

      // First call with commit_block_list=false
      await uploadAppendFile(
        formData,
        totalChunks,
        totalChunks,
        selectedTrain,
        false,
        lastUploadedDataset?.appendId,
      );

      // Second call with commit_block_list=true
      const response = await uploadAppendFile(
        formData,
        totalChunks,
        totalChunks,
        selectedTrain,
        true,
        lastUploadedDataset?.appendId,
      );

      if (response && response.status) {
        if (response.status === 200) {
          const { file_name, append_id } = response;
          setLastUploadedDataset({
            fileName: file_name,
            appendId: append_id,
          });
          return {
            success: true,
            data: response,
            message: response.message,
          };
        } else if (response.status === 422 || response.status === 400) {
          const errorMessage =
            response.detail?.error ||
            response.detail ||
            t("dataAppend.invalidFile");
          const errorKey = response.detail?.key;
          if (errorKey === "MISSING_COLUMNS") {
            const errorInfo = parseErrorMessage(errorMessage);
            const formattedContent = renderFormattedError(errorInfo);
            return {
              error: errorMessage,
              status: response.status,
              formattedContent,
            };
          }
          return {
            error: errorMessage,
            status: response.status,
          };
        } else {
          return {
            error: t("ValueStrings.somethingWentWrong"),
            status: response.status,
          };
        }
      } else {
        return { error: t("dataAppend.uploadError") };
      }
    } catch (error) {
      console.error("Upload failed:", error);

      return { error: error.message || t("dataAppend.uploadError") };
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileValidation = async () => {
    if (uploadedFile) {
      const uploadResult = await uploadFile(uploadedFile);
      onFileValidation?.(uploadResult);
      if (uploadResult && uploadResult.success) {
        setIsUploadComplete(true);
        return { success: true };
      } else if (uploadResult && uploadResult.error) {
        setIsUploadComplete(false);
        return { error: uploadResult.error };
      } else {
        setIsUploadComplete(false);
        return { error: t("dataAppend.uploadError") };
      }
    }

    return { error: t("dataAppend.noFileError") };
  };

  const handleAppendClick = async () => {
    await handleAppendExistingFile();
  };

  useEffect(() => {
    if (lastUploadedDataset) {
      setIsUploadComplete(true);
      setUploadedFile({ name: lastUploadedDataset.fileName });
    } else {
      setUploadedFile(null);
      setIsUploadComplete(false);
    }
  }, [lastUploadedDataset]);

  return (
    <div className="file-handler-container">
      {uploadedFile ? (
        <FileUploadCard
          uploadedFile={uploadedFile}
          setUploadedFile={handleFileUpload}
          onValidHandler={handleFileValidation}
          onAppendHandler={handleAppendClick}
          isLoading={isUploading}
          isUploadComplete={isUploadComplete}
          showAppend={true}
          showRemoveIcon={true}
          handleRemoveFile={handleAppendRemove}
        />
      ) : (
        <FileSelectionCard
          id="file-upload-dialog"
          setUploadedFile={handleFileUpload}
          isProceed={isProceed}
          setIsProceed={setIsProceed}
          hoverable={true}
          disabled={false}
          size="115px"
        />
      )}

      <AppendInfoSection />
    </div>
  );
};

export default FileHandler;
